import React, { useLayoutEffect } from 'react';
import { hydrate } from 'react-dom';
import { BrowserRouter as Router } from '@react-router';
import { loadableReady } from '@loadable/component';
import { useSSR } from 'react-i18next';
import { ErrorBoundary } from './ErrorBoundary';
import globalWindow from '@shared/core/globals';
import App from './App';
import '@shared/core/i18n/i18n';
import { FullPageLoaderProvider } from '@shared/components/JoyLogoLoader/FullPageLoaderProvider';

type HydrationSuccessProps = Readonly<{
  children: React.ReactNode;
}>;

/**
 * Signals the hydration watchdog (inline script in <head>) that React
 * mounted successfully. Placed INSIDE the ErrorBoundary so it only
 * mounts when hydration succeeds without errors. If the ErrorBoundary
 * catches a render error (e.g. a chunk load failure), this component
 * is replaced by the fallback UI and never mounts, so the watchdog
 * stays armed and eventually redirects to ?retry=page.
 */
const HydrationSuccess: React.FC<HydrationSuccessProps> = ({ children }) => {
  useLayoutEffect(() => {
    // Cancel the watchdog's ?retry=page redirect timer synchronously
    // during commit, before React yields to the browser. This closes
    // the window where a deferred useEffect could let the watchdog
    // fire on a successfully hydrated page. Undefined on retry pages
    // (the watchdog isn't injected there), hence the optional call.
    window.__JOY_CANCEL_WATCHDOG__?.();
    // Same commit-only semantics as the watchdog above: a hydration failure must leave the
    // script monitor armed so its stall sweep still reports (PRNT-4988).
    window.__JOY_CANCEL_SCRIPT_MONITOR__?.();
  }, []);
  return <>{children}</>;
};

const BaseApp: React.FC = props => {
  useSSR(globalWindow.__INITIAL_I18N_STORE__, globalWindow.__INITIAL_I18N_LANG__);

  return (
    <ErrorBoundary>
      <HydrationSuccess>
        <FullPageLoaderProvider>
          <Router>
            <App
              {...props}
              defaultScreen={globalWindow.__INITIAL_DEVICE_SCREEN__}
            />
          </Router>
        </FullPageLoaderProvider>
      </HydrationSuccess>
    </ErrorBoundary>
  );
};

// Wait for all scripts to load asynchronously
loadableReady(() => {
  const root = document.getElementById('root');
  hydrate(<BaseApp />, root);
  // NOTE: neither __JOY_CANCEL_WATCHDOG__ nor __JOY_CANCEL_SCRIPT_MONITOR__ (PRNT-4988) is
  // called here. Both are called from the HydrationSuccess component's useLayoutEffect,
  // which only fires when the app tree commits successfully (i.e. the ErrorBoundary did NOT
  // catch an error). loadableReady resolving is not that signal — it fires whether or not
  // hydrate() actually commits without throwing. Cancelling here instead would disarm both
  // the watchdog and the script monitor on a hydration FAILURE too — exactly when the watchdog
  // still needs to redirect to ?retry=page and the script monitor still needs to keep reporting.
});

if (process.env.NODE_ENV === 'development') {
  module.hot?.accept();
}
